import { normalizeFieldFromServer } from "@/utils/waajoFormsSchema";

export function isEmptyResponseValue(value) {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === "string") {
    return value.trim() === "";
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

export function formatResponseDateOnly(value) {
  const text = String(value || "").trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return text;
  }
  return match[3] + "/" + match[2] + "/" + match[1];
}

function optionLabel(field, value) {
  const options = field && Array.isArray(field.options) ? field.options : [];
  for (let index = 0; index < options.length; index += 1) {
    if (String(options[index].value) === String(value)) {
      return options[index].label || options[index].value || "";
    }
  }
  return "";
}

export function formatResponseAnswer(field, value) {
  if (isEmptyResponseValue(value)) {
    return "Tidak diisi";
  }

  const type = field && field.type ? field.type : "text";

  if (type === "checkbox") {
    const values = Array.isArray(value) ? value : [value];
    const labels = values
      .map(function(item) {
        return optionLabel(field, item) || String(item);
      })
      .filter(function(item) {
        return item !== "";
      });
    return labels.length ? labels.join(", ") : "Tidak diisi";
  }

  if (Array.isArray(value)) {
    return value.length ? value.map(String).join(", ") : "Tidak diisi";
  }

  if (type === "radio" || type === "dropdown") {
    return optionLabel(field, value) || String(value);
  }

  if (type === "date") {
    return formatResponseDateOnly(value);
  }

  if (typeof value === "boolean") {
    return value ? "Ya" : "Tidak";
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }

  return String(value);
}

export function getRespondentText(item) {
  const phone = item && item.respondent_phone ? String(item.respondent_phone).trim() : "";
  const email = item && item.respondent_email ? String(item.respondent_email).trim() : "";
  const parts = [];
  if (phone) {
    parts.push(phone);
  }
  if (email) {
    parts.push(email);
  }
  return parts.length ? parts.join(" • ") : "Tidak dicantumkan";
}

export function getPreviewText(item) {
  if (!item) {
    return "Tidak diisi";
  }
  return formatResponseAnswer(
    { type: item.type || "text", options: [] },
    item.value
  );
}

export function getShortResponseId(id) {
  const value = String(id || "").trim();
  if (!value) {
    return "-";
  }
  return value.slice(0, 8);
}

export function getDownloadFilename(contentDisposition, fallback) {
  const header = String(contentDisposition || "").trim();
  if (header) {
    const encoded = header.match(/filename\*=UTF-8''([^;]+)/i);
    if (encoded && encoded[1]) {
      try {
        return sanitizeDownloadFilename(decodeURIComponent(encoded[1]), fallback);
      } catch (error) {
        // fallback below
      }
    }
    const plain = header.match(/filename="?([^";]+)"?/i);
    if (plain && plain[1]) {
      return sanitizeDownloadFilename(plain[1], fallback);
    }
  }
  return sanitizeDownloadFilename(fallback, "respons-form.xlsx");
}

export function buildExportFallbackFilename(formName) {
  const slug = String(formName || "form")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "form";
  const now = new Date();
  const date =
    now.getFullYear() +
    String(now.getMonth() + 1).padStart(2, "0") +
    String(now.getDate()).padStart(2, "0");
  const time =
    String(now.getHours()).padStart(2, "0") +
    String(now.getMinutes()).padStart(2, "0") +
    String(now.getSeconds()).padStart(2, "0");
  return "respons-" + slug + "-" + date + "-" + time + ".xlsx";
}

function sanitizeDownloadFilename(value, fallback) {
  let name = String(value || "")
    .replace(/[\r\n"]/g, "")
    .replace(/[\\/:*?<>|]+/g, "-")
    .trim();
  if (!name) {
    name = String(fallback || "respons-form.xlsx");
  }
  if (!/\.xlsx$/i.test(name)) {
    name += ".xlsx";
  }
  return name;
}

export function getSnapshotFields(detail) {
  const status =
    detail && detail.schema_snapshot_status
      ? detail.schema_snapshot_status
      : "missing";

  if (!detail || !detail.schema_snapshot_json) {
    return { fields: [], status: status, available: false };
  }

  let snapshot = detail.schema_snapshot_json;
  if (typeof snapshot === "string") {
    try {
      snapshot = JSON.parse(snapshot);
    } catch (error) {
      return { fields: [], status: "invalid", available: false };
    }
  }

  if (!snapshot || !Array.isArray(snapshot.fields)) {
    return { fields: [], status: "invalid", available: false };
  }

  return {
    fields: snapshot.fields.map(function(field) {
      return normalizeFieldFromServer(field);
    }),
    status: status,
    available: true
  };
}

export function buildResponseDetailRows(detail) {
  if (!detail || !detail.answers || typeof detail.answers !== "object") {
    return [];
  }

  const snapshot = getSnapshotFields(detail);
  const fields = snapshot.fields.slice();
  const knownKeys = {};

  fields.forEach(function(field) {
    knownKeys[field.key] = true;
  });

  Object.keys(detail.answers).forEach(function(key) {
    if (knownKeys[key]) {
      return;
    }
    fields.push({
      _uid: "fallback_" + key,
      key: key,
      type: "text",
      label: key,
      required: false,
      placeholder: "",
      options: []
    });
  });

  return fields.map(function(field) {
    const value = detail.answers[field.key];
    return {
      key: field.key,
      label: field.label || field.key,
      type: field.type,
      value: value,
      displayValue: formatResponseAnswer(field, value)
    };
  });
}



// WEBPACK FOOTER //
// ./src/utils/waajoFormsResponses.js